import { useEffect, useRef, useState } from "react";
import { ReceiptInput } from "./ui/ReceiptInput.js";
import { Summary } from "./ui/Summary.js";
import { FlagTable } from "./ui/FlagTable.js";
import { Warnings } from "./ui/Warnings.js";
import { ActionTimeline } from "./ui/ActionTimeline.js";
import { CompletenessAffordance } from "./ui/CompletenessAffordance.js";
import { VerificationLevelLegend } from "./ui/VerificationLevelLegend.js";
import { Footer } from "./ui/Footer.js";
import {
  runVerification,
  type CompletenessVerificationOptions,
  type InclusionProofFetchOptions,
  type RunVerificationResult
} from "./verify/run.js";
import { evaluateKeyFormat } from "./key-format.js";
import {
  fetchTrustedWitnessKeys,
  fetchTrustedRegistrationKeys,
  fetchTrustedAuthorKeys,
  TrustAnchorFetchError
} from "./verify/trusted-keys.js";
import type { LoaderResult } from "./load/types.js";

const DEFAULT_WITNESS_URL = "https://witness.sequesign.com";
const DEFAULT_DASHBOARD_API_URL = "https://dashboard-api.sequesign.com";

export function App() {
  const [result, setResult] = useState<RunVerificationResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const [completenessEnabled, setCompletenessEnabled] = useState(false);
  const [witnessUrl, setWitnessUrl] = useState(DEFAULT_WITNESS_URL);
  const [completenessKey, setCompletenessKey] = useState("");
  const [lastLoaded, setLastLoaded] = useState<LoaderResult | null>(null);
  // The completeness settings as they were when the displayed result was
  // produced. The affordance classifies the result against this snapshot,
  // not the live controls, so editing the controls after a run does not
  // silently reclassify an already-displayed result (it only changes on a
  // re-run). Null whenever there is no current result.
  const [resultSettings, setResultSettings] = useState<{
    completenessEnabled: boolean;
    witnessUrl: string;
    completenessKey: string;
  } | null>(null);

  // The result section is scrolled into view when a result lands; the
  // controls section and checkbox are scroll/focus targets for the
  // completeness affordance's "back to controls" button.
  const resultRef = useRef<HTMLElement>(null);
  const controlsRef = useRef<HTMLElement>(null);
  const completenessCheckboxRef = useRef<HTMLInputElement>(null);

  // Bring the result into view whenever a verification lands, including
  // re-runs (result is a fresh object each run, so the reference changes).
  // No-op while result is still null.
  useEffect(() => {
    if (!result) return;
    resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [result]);

  // Scroll the completeness controls back into view and move keyboard
  // focus to the toggle. The short delay lets the smooth scroll land
  // before the focus ring appears, rather than during the scroll.
  function onScrollToControls() {
    controlsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    setTimeout(() => {
      completenessCheckboxRef.current?.focus();
    }, 300);
  }

  // Live format feedback, derived from the current input on every render.
  const keyFormat = evaluateKeyFormat(completenessKey);

  async function runWithCurrentLoaded(loaderResult: LoaderResult) {
    if (loaderResult.error || !loaderResult.receipt) {
      setRunError(loaderResult.error?.message ?? null);
      return;
    }
    setBusy(true);
    setRunError(null);
    // Snapshot the completeness settings driving this run so the result is
    // classified by what produced it, not by later edits to the controls.
    const settingsForRun = { completenessEnabled, witnessUrl, completenessKey };
    try {
      let completeness: CompletenessVerificationOptions | undefined;
      if (
        settingsForRun.completenessEnabled &&
        settingsForRun.witnessUrl.trim() &&
        settingsForRun.completenessKey.trim()
      ) {
        // Format is validated live in the input; the witness makes the
        // authoritative read-only/existence decision on the request.
        completeness = {
          baseUrl: settingsForRun.witnessUrl.trim(),
          apiKey: settingsForRun.completenessKey.trim()
        };
      }
      // Online inclusion-proof fetch is on by default whenever a
      // witness URL is configured. The verifier always tries the
      // embedded proof and sidecar first; the fetcher is only
      // consulted for attestations that have neither. The inclusion
      // endpoint is public (protocol-spec section 16.6) so no API
      // key is sent. Inclusion verification works for any receipt
      // dropped into the verifier, regardless of whether the
      // reviewer has access to the customer's API key.
      const inclusionProofs: InclusionProofFetchOptions | undefined =
        settingsForRun.witnessUrl.trim()
          ? { baseUrl: settingsForRun.witnessUrl.trim() }
          : undefined;
      // Trust anchor: fetch the witness's well-known keys (cached per
      // session) and verify against them. Verification refuses to run if
      // the anchor cannot be fetched; it never falls back to the key
      // embedded in the receipt.
      const trustedWitnessKeys = await fetchTrustedWitnessKeys(
        settingsForRun.witnessUrl.trim() || DEFAULT_WITNESS_URL
      );
      // Registration keys are best-effort (see fetchTrustedRegistrationKeys):
      // they set the agent-identity tier, not a required anchor, so a fetch
      // failure degrades to self_asserted rather than blocking verification.
      const trustedRegistrationKeys = await fetchTrustedRegistrationKeys(DEFAULT_DASHBOARD_API_URL);
      // Template-author keys are best-effort (see fetchTrustedAuthorKeys): they
      // grade template_authenticity, not a required anchor, so a fetch failure or
      // empty document degrades to unrecognized/unattested rather than blocking.
      const trustedAuthorKeys = await fetchTrustedAuthorKeys(DEFAULT_DASHBOARD_API_URL);
      const next = await runVerification(loaderResult.receipt, {
        trustedWitnessKeys,
        trustedRegistrationKeys,
        trustedAuthorKeys,
        completeness,
        inclusionProofs
      });
      setResult(next);
      setResultSettings(settingsForRun);
    } catch (err) {
      setRunError(
        // The trust-anchor fetch failure is already plain-language copy;
        // surface it verbatim rather than wrapping it.
        err instanceof TrustAnchorFetchError
          ? err.message
          : err instanceof Error
            ? `Verification could not run: ${err.message}`
            : "Verification could not run for an unknown reason."
      );
      setResult(null);
      setResultSettings(null);
    } finally {
      setBusy(false);
    }
  }

  async function handleLoaded(loaderResult: LoaderResult) {
    setLastLoaded(loaderResult);
    await runWithCurrentLoaded(loaderResult);
  }

  function handleClear() {
    setResult(null);
    setResultSettings(null);
    setRunError(null);
    setBusy(false);
    setLastLoaded(null);
  }

  function handleRerun() {
    if (lastLoaded) runWithCurrentLoaded(lastLoaded);
  }

  return (
    <div className="app">
      <header className="header">
        <a href="https://sequesign.com" className="wordmark-link" aria-label="Sequesign home">
          <img
            src="/sequesign-logo-dark.svg"
            alt="Sequesign"
            className="wordmark-mark"
            width="174"
            height="36"
          />
          <span className="wordmark">Receipt Verifier</span>
        </a>
        <p className="tagline">
          Cryptographically verifiable receipts of delegated AI work. Drop a receipt package here to
          check what was recorded, what was signed, and what the receipt does not claim.
        </p>
      </header>

      <section className="section">
        <h2 className="section-title">Provide a receipt</h2>
        <ReceiptInput onLoaded={handleLoaded} onClear={handleClear} busy={busy} />
        {runError ? <div className="loader-error">{runError}</div> : null}
      </section>

      <section className="section" ref={controlsRef}>
        <h2 className="section-title">Optional online check</h2>
        <p className="dropzone-secondary">
          Verification runs offline by default. To confirm the receipt records every action the
          witness captured for this chain, toggle the online completeness check below. The verifier
          will query the witness service for the entries it holds for this chain and compare to the
          receipt.
        </p>
        <div className="completeness-controls">
          <label className="completeness-toggle">
            <input
              ref={completenessCheckboxRef}
              type="checkbox"
              checked={completenessEnabled}
              onChange={(e) => setCompletenessEnabled(e.target.checked)}
            />
            <span>Verify completeness against witness log</span>
          </label>
          {completenessEnabled ? (
            <div className="completeness-inputs">
              <input
                type="text"
                value={witnessUrl}
                onChange={(e) => setWitnessUrl(e.target.value)}
                placeholder="https://witness.sequesign.com"
                aria-label="Witness service URL"
              />
              <input
                type="password"
                value={completenessKey}
                onChange={(e) => setCompletenessKey(e.target.value)}
                placeholder="sq_live_ro_..."
                aria-label="Read-only API key"
              />
              {keyFormat.kind === "empty" ? (
                <div className="key-format-hint">
                  Use a read-only API key. Format: sq_live_ro_... or sq_test_ro_... The key is
                  required for the completeness check only. Inclusion proofs are retrieved from the
                  witness's public endpoint and do not require a key.
                </div>
              ) : keyFormat.kind === "valid" ? (
                <div className="key-format-valid">
                  Key format valid. The witness checks the key when you re-run.
                </div>
              ) : (
                <div className="key-format-invalid">{keyFormat.message}</div>
              )}
              <button
                type="button"
                className="input-button"
                disabled={!lastLoaded || busy || keyFormat.kind !== "valid"}
                onClick={handleRerun}
              >
                Re-run with completeness
              </button>
              {result?.report.flags.completeness_verified === true ? (
                <div className="key-format-valid">Verified completeness using this key.</div>
              ) : null}
            </div>
          ) : null}
        </div>
      </section>

      {result ? (
        <>
          <section className="section" ref={resultRef}>
            <h2 className="section-title">Verification result</h2>
            <Summary
              report={result.report}
              receipt={result.receipt}
              sourceLabel={result.sourceLabel}
              actionCount={result.actions.length}
            />
            <CompletenessAffordance
              result={result}
              completenessEnabled={resultSettings?.completenessEnabled ?? false}
              witnessUrl={resultSettings?.witnessUrl ?? ""}
              completenessKey={resultSettings?.completenessKey ?? ""}
              runError={runError}
              onScrollToControls={onScrollToControls}
            />
          </section>

          <section className="section">
            <h2 className="section-title">Verification flags</h2>
            <FlagTable report={result.report} />
          </section>

          <Warnings actions={result.actions} evidence={result.evidence} />

          <section className="section">
            <h2 className="section-title">Action timeline</h2>
            <ActionTimeline
              actions={result.actions}
              evidence={result.evidence}
              receipt={result.receipt}
            />
          </section>
        </>
      ) : null}

      <section className="section">
        <VerificationLevelLegend />
      </section>

      <Footer />
    </div>
  );
}
